const PLACE_FIELDS = [
    'displayName',
    'formattedAddress',
    'location',
    'id',
    'googleMapsURI',
    'regularOpeningHours',
    'addressComponents',
    'editorialSummary',
    'nationalPhoneNumber',
    'websiteURI',
];

const PLACE_ID_PATTERN = /ChIJ[A-Za-z0-9_-]{10,}/;

const readText = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.text || '';
};

const readLatLng = (location) => {
    if (!location) return { lat: null, lng: null };
    const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
    const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
    return {
        lat: Number.isFinite(Number(lat)) ? Number(lat) : null,
        lng: Number.isFinite(Number(lng)) ? Number(lng) : null,
    };
};

const isValidCoordinate = (lat, lng) =>
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180;

const distanceMeters = (from, to) => {
    const earthRadius = 6371000;
    const toRadians = (degrees) => (degrees * Math.PI) / 180;
    const latDelta = toRadians(to.lat - from.lat);
    const lngDelta = toRadians(to.lng - from.lng);
    const lat1 = toRadians(from.lat);
    const lat2 = toRadians(to.lat);
    const haversine =
        Math.sin(latDelta / 2) ** 2 +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(lngDelta / 2) ** 2;
    return 2 * earthRadius * Math.asin(Math.min(1, Math.sqrt(haversine)));
};

const componentText = (component) => component?.longText || component?.shortText || '';

const cityFromComponents = (components = []) => {
    const find = (type) => components.find((component) => component.types?.includes(type));
    const locality = find('locality');
    const sublocality = find('sublocality_level_1');
    const adminArea2 = find('administrative_area_level_2');
    const adminArea1 = find('administrative_area_level_1');
    const match = locality || sublocality || adminArea2 || adminArea1;
    return componentText(match);
};

const stateFromComponents = (components = []) => {
    const match = components.find((component) => component.types?.includes('administrative_area_level_1'));
    return componentText(match).replace(/^provincia\s+(de|del)\s+/i, '').trim();
};

const normalizeComponents = (components = []) =>
    components.map((component) => ({
        longText: component.longText || component.long_name || '',
        shortText: component.shortText || component.short_name || '',
        types: component.types || [],
    }));

const withProtocol = (value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
};

export const parseGoogleMapsUrl = (rawUrl) => {
    const original = (rawUrl || '').trim();
    const normalized = withProtocol(original);
    const empty = {
        isMapsUrl: false,
        isShort: false,
        placeId: '',
        name: '',
        lat: null,
        lng: null,
        url: original,
    };

    let parsed;
    try {
        parsed = new URL(normalized);
    } catch {
        return empty;
    }

    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    const isShort = host === 'maps.app.goo.gl' || (host === 'goo.gl' && parsed.pathname.startsWith('/maps'));
    const isMapsHost =
        isShort ||
        host === 'maps.google.com' ||
        ((host === 'google.com' || host.endsWith('.google.com')) && parsed.pathname.includes('/maps'));

    if (!isMapsHost) return empty;

    let placeId = '';
    const queryPlaceId = parsed.searchParams.get('query_place_id') || parsed.searchParams.get('place_id');
    if (queryPlaceId && PLACE_ID_PATTERN.test(queryPlaceId)) {
        placeId = queryPlaceId.match(PLACE_ID_PATTERN)[0];
    }
    if (!placeId) {
        const embeddedPlaceId = normalized.match(/place_id:(ChIJ[A-Za-z0-9_-]{10,})/);
        placeId = embeddedPlaceId?.[1] || normalized.match(PLACE_ID_PATTERN)?.[0] || '';
    }

    let lat = null;
    let lng = null;
    const precisePin = normalized.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
    const atPin = normalized.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
    const coordinateMatch = precisePin || atPin;
    if (coordinateMatch) {
        lat = Number(coordinateMatch[1]);
        lng = Number(coordinateMatch[2]);
    }

    if (!isValidCoordinate(lat, lng)) {
        const queryValue = parsed.searchParams.get('q') || parsed.searchParams.get('ll') || parsed.searchParams.get('query') || '';
        const queryCoordinates = queryValue.match(/^(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)$/);
        if (queryCoordinates) {
            lat = Number(queryCoordinates[1]);
            lng = Number(queryCoordinates[2]);
        }
    }

    if (!isValidCoordinate(lat, lng)) {
        lat = null;
        lng = null;
    }

    let name = '';
    const placePath = parsed.pathname.match(/\/maps\/place\/([^/]+)/);
    if (placePath) {
        name = decodeURIComponent(placePath[1].replace(/\+/g, ' ')).trim();
    }
    if (!name) {
        const queryName = parsed.searchParams.get('q') || parsed.searchParams.get('query') || '';
        if (queryName && !/^-?\d+(?:\.\d+)?,\s*-?\d+(?:\.\d+)?$/.test(queryName) && !queryName.startsWith('place_id:')) {
            name = decodeURIComponent(queryName).trim();
        }
    }

    return {
        isMapsUrl: true,
        isShort,
        placeId,
        name,
        lat,
        lng,
        url: original,
    };
};

const normalizePlace = (place, sourceUrl) => {
    const { lat, lng } = readLatLng(place.location);
    const components = normalizeComponents(place.addressComponents);
    const placeId = String(place.id || '').replace(/^places\//, '');

    return {
        name: readText(place.displayName),
        address: place.formattedAddress || '',
        lat,
        lng,
        city: cityFromComponents(components),
        state: stateFromComponents(components),
        phoneNumber: place.nationalPhoneNumber || '',
        websiteURI: place.websiteURI || '',
        editorialSummary: readText(place.editorialSummary),
        openingHours: place.regularOpeningHours?.weekdayDescriptions?.filter(Boolean) || [],
        googlePlaceId: placeId,
        googleMapsUrl: sourceUrl,
        fromBusiness: true,
    };
};

const isUsefulPlace = (place) =>
    Boolean(place?.address) || isValidCoordinate(place?.lat, place?.lng);

const geocodeLocation = (geocoder, location) =>
    new Promise((resolve, reject) => {
        geocoder.geocode({ location }, (results, status) => {
            if (status === 'OK' && results?.length) {
                resolve(results[0]);
                return;
            }
            reject(new Error(status || 'GEOCODE_FAILED'));
        });
    });

const lookupByPlaceId = async (Place, placeId) => {
    const place = new Place({ id: placeId });
    await place.fetchFields({ fields: PLACE_FIELDS });
    return normalizePlace(place, '');
};

const lookupByName = async (Place, parsed) => {
    const request = {
        textQuery: parsed.name,
        fields: PLACE_FIELDS,
        maxResultCount: 1,
        language: 'es',
        region: 'ec',
    };

    if (isValidCoordinate(parsed.lat, parsed.lng)) {
        request.locationBias = {
            center: { lat: parsed.lat, lng: parsed.lng },
            radius: 500,
        };
    }

    const { places } = await Place.searchByText(request);
    const match = places?.[0];
    if (!match) return null;

    const resolved = normalizePlace(match, parsed.url);
    if (!isValidCoordinate(resolved.lat, resolved.lng) && isValidCoordinate(parsed.lat, parsed.lng)) {
        resolved.lat = parsed.lat;
        resolved.lng = parsed.lng;
    }
    if (!isValidCoordinate(parsed.lat, parsed.lng) || !isValidCoordinate(resolved.lat, resolved.lng)) {
        return resolved;
    }

    if (distanceMeters(parsed, resolved) > 1500) return null;
    return resolved;
};

const lookupByCoordinates = async (parsed) => {
    const { Geocoder } = await window.google.maps.importLibrary('geocoding');
    const geocoder = new Geocoder();
    const result = await geocodeLocation(geocoder, { lat: parsed.lat, lng: parsed.lng });
    const components = normalizeComponents(result.address_components);
    const point = readLatLng(result.geometry?.location);

    return {
        name: parsed.name || '',
        address: result.formatted_address || '',
        lat: point.lat ?? parsed.lat,
        lng: point.lng ?? parsed.lng,
        city: cityFromComponents(components),
        state: stateFromComponents(components),
        phoneNumber: '',
        websiteURI: '',
        editorialSummary: '',
        openingHours: [],
        googlePlaceId: result.place_id || '',
        googleMapsUrl: parsed.url,
        fromBusiness: false,
    };
};

export const resolveGoogleMapsUrl = async (rawUrl) => {
    const parsed = parseGoogleMapsUrl(rawUrl);

    if (!parsed.isMapsUrl) {
        throw new Error('Pega un enlace de Google Maps.');
    }

    if (parsed.isShort && !parsed.placeId && parsed.lat == null) {
        throw new Error(
            'Ese enlace está abreviado. Ábrelo en el navegador, copia la dirección completa de la barra y pégala aquí.'
        );
    }

    if (!parsed.placeId && !parsed.name && parsed.lat == null) {
        throw new Error('No pude leer la ubicación de ese enlace. Usa el enlace completo del lugar.');
    }

    if (!window.google?.maps?.importLibrary) {
        throw new Error('Google Maps todavía no carga. Espera un momento y vuelve a intentar.');
    }

    let resolved = null;

    try {
        const { Place } = await window.google.maps.importLibrary('places');

        if (parsed.placeId) {
            try {
                const byId = await lookupByPlaceId(Place, parsed.placeId);
                byId.googleMapsUrl = parsed.url;
                if (!byId.name) byId.name = parsed.name;
                if (!isValidCoordinate(byId.lat, byId.lng)) {
                    byId.lat = parsed.lat;
                    byId.lng = parsed.lng;
                }
                if (isUsefulPlace(byId)) resolved = byId;
            } catch (error) {
                console.error('No se pudo consultar el lugar por id', error);
            }
        }

        if (!resolved && parsed.name) {
            try {
                resolved = await lookupByName(Place, parsed);
            } catch (error) {
                console.error('No se pudo buscar el lugar por nombre', error);
            }
        }

        if (!resolved && isValidCoordinate(parsed.lat, parsed.lng)) {
            try {
                resolved = await lookupByCoordinates(parsed);
            } catch (error) {
                console.error('No se pudo geocodificar el enlace', error);
            }
        }
    } catch (error) {
        console.error('No se pudo consultar Google Maps', error);
    }

    if (!isUsefulPlace(resolved)) {
        throw new Error('No se pudo consultar Google Maps. Revisa el enlace o que Places API esté habilitada.');
    }

    return resolved;
};
